<!--赖传峰-->
<!--2022/9/2-->
<script
  lang="ts"
  setup
>
import { computed } from '#imports'
import CloseIcon from '@/components/icons/CloseIcon.vue'
import EllipsisIcon from '@/components/icons/EllipsisIcon.vue'
import SelectAllIcon from '@/components/icons/SelectAllIcon.vue'
import SelectModeIcon from '@/components/icons/SelectModeIcon.vue'
import FileExplorerViewType from '@/components/share/FileExplorerViewType.vue'
import { useLayout } from '@/store/layout'
import { useShare } from '@/store/share'
import { ViewType } from '@/types/enum'
import ViewTypeBase from '~/components/share/ViewTypeBase.vue'
import useDisplayMode from '~/composables/useDisplayMode'
import { TOOLTIP_COMMON_PROPS } from '~/types/constants'
import FileExplorerSorter from './FileExplorerSorter.vue'
import FolderNavigator from './FolderNavigator.vue'
import OperationContainer from './OperationContainer.m.vue'

const shareStore = useShare()
const layoutStore = useLayout()

const isMobile = computed(() => layoutStore.isMobile)
defineProps<{
  mobileSelectMode: boolean
  isSelectedAll: boolean
  handleSelectAll(): void
  handleOpenFile(): void
  handleRestore(): void
  loadShareData?: () => void
}>()

const setMobileSelectMode = (val: boolean): void => { shareStore.mobileSelectMode = val }

const { viewType } = useDisplayMode()
</script>
<template>
  <div
    :class="viewType"
    class="files-header"
  >
    <FolderNavigator
      v-show="!mobileSelectMode"
      :view-type="viewType"
    />
    <template v-if="isMobile">
      <div
        id="multi_operation"
        :style="mobileSelectMode ? 'flex: auto;' : undefined"
        class="multi-operation"
      >
        <!-- Left Range -->
        <div v-show="mobileSelectMode">
          <a
            class="pp-link-button"
            @click="setMobileSelectMode(false)"
          >
            <CloseIcon :size="26" />
          </a>
          <span>{{ shareStore.selectedIds.length }}</span>
        </div>
        <!-- Right Range -->
        <div>
          <a
            v-if="mobileSelectMode"
            class="pp-link-button"
            @click="handleSelectAll"
          >
            <SelectAllIcon />
          </a>
          <div
            v-show="mobileSelectMode"
            class="divider"
          />
          <ClientOnly>
            <OperationContainer
              :register-groups="['multiOperationGroup', 'sorterGroup', 'viewModeGroup']"
            >
              <template #multiOperationGroup="$slotProps">
                <div
                  :class="[$slotProps.class]"
                  @click="setMobileSelectMode(true);$slotProps.onClick()"
                >
                  <SelectModeIcon />
                  <span>{{ $t('select-text') }}</span>
                </div>
              </template>
              <template #sorterGroup="$slotProps">
                <FileExplorerSorter
                  show-shim
                  :load-share-data="loadShareData"
                  @after-sort="$slotProps.onClick"
                />
              </template>
              <template #viewModeGroup="$slotProps">
                <ViewTypeBase
                  @change="() => $slotProps.onClick()"
                />
              </template>
            </OperationContainer>

            <template #fallback>
              <a
                class="pp-link-button"
                style="width: 48px;"
              >
                <EllipsisIcon
                  color="#666666"
                  style="transform: rotate(90deg);"
                />
              </a>
            </template>
          </ClientOnly>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        id="multi_operation"
        :style="mobileSelectMode ? 'flex: auto;' : undefined"
        class="multi-operation"
      >
        <div>
          <div
            v-if="shareStore.selectedIds.length > 0"
            class="select-op-box"
          >
            <span>{{ $t('BgG8qRhjke3f7gTLE6', [shareStore.selectedIds.length]) }}</span>
            <ElTooltip
              :content="isSelectedAll ? $t('qXgQVGdbxePGc2hzTt') : $t('FLnPBWd79Njw9F2WVW')"
              :disabled="isMobile"
              :offset="8"
              v-bind="TOOLTIP_COMMON_PROPS"
            >
              <span
                :class="['pp-checkbox', isSelectedAll ? 'checked' : 'indeterminate']"
                @click="handleSelectAll"
              />
            </ElTooltip>
          </div>
          <ul class="file-operations">
            <li>
              <FileExplorerSorter :load-share-data="loadShareData" />
            </li>
            <li>
              <ElTooltip
                :content="$t('toggle-file-explorer-view')"
                :disabled="isMobile"
                :offset="0"
                v-bind="TOOLTIP_COMMON_PROPS"
              >
                <div class="trigger">
                  <FileExplorerViewType />
                </div>
              </ElTooltip>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </div>

  <div
    v-if="!isMobile && viewType === ViewType.LIST"
    class="grid-list-header"
  >
    <div class="info">
      {{ $t('name') }}
    </div>
    <div class="size">
      {{ $t('size') }}
    </div>
    <div class="modified-time">
      {{ $t('modified-time') }}
    </div>
    <!-- 占位元素，没有标题 -->
    <div class="operation" />
  </div>
</template>

<style
  lang="less"
  scoped
>
@import '@/assets/styles/util.less';

.files-header {
  flex: 0 0 36px;
  padding: 0 2px 0 var(--padding);
  line-height: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 6;

  .nav {
    flex: 1 1 auto;
    min-width: 0;
  }

  .multi-operation {
    display: flex;
    align-items: center;
    justify-content: space-between;

    a {
      width: 48px;
    }

    > div {
      display: flex;
    }

    .divider {
      width: 1px;
      height: 18px;
      background-color: #98a2ad66;
      align-self: center;
    }
  }

  .select-op-box {
    display: flex;
    align-items: center;
    gap: 8px;

    &::after {
      display: block;
      content: '';
      width: 1px;
      height: 18px;
      background-color: #98a2ad66;
      margin-right: 24px;
      margin-left: 16px;
    }

    .pp-checkbox {
      cursor: pointer;
    }
  }

  .file-operations {
    display: flex;
    align-items: center;
  }
}

.grid-list-header {
  display: flex;
  margin-top: 16px;
  height: 24px;
  font-size: 13px;
  color: #98a2ad;
  position: relative;
  line-height: 26px;
  -webkit-touch-callout: none;
  user-select: none;
  padding-left: 96px;

  & > * {
    padding: 0 8px;
    display: flex;
    align-items: center;
  }

  .info {
    flex: auto;
  }

  .size {
    width: 110px;
    font-size: 13px;
    padding-left: 28px;
  }

  .modified-time {
    width: 150px;
    font-size: 13px;
    padding-left: 28px;
  }

  .operation {
    width: 189px;
  }
}

@media screen and (min-width: @mobile-break-width) {
  .files-header {
    padding: 0 10px;
    height: 54px;
    margin: 16px 18px 0;
    position: relative;

  }
}
</style>
